import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Languages } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { useVoiceStore } from "@/stores/voiceStore";
import { cn } from "@/lib/cn";
import type { VoiceGender, VoiceReplyTrigger } from "@/stores/voiceStore";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const PAGE_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { ...SPRING, staggerChildren: 0.05 },
  },
};

const SECTION_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: SPRING },
};

const COLLAPSE_VARIANTS = {
  hidden: { opacity: 0, height: 0, overflow: "hidden" as const },
  visible: {
    opacity: 1,
    height: "auto",
    overflow: "visible" as const,
    transition: { ...SPRING, opacity: { duration: 0.2 } },
  },
  exit: {
    opacity: 0,
    height: 0,
    overflow: "hidden" as const,
    transition: { duration: 0.2 },
  },
};

const VOICE_OPTIONS: { gender: VoiceGender; name: string; tone: string }[] = [
  { gender: "male", name: "Abebe", tone: "Professional" },
  { gender: "female", name: "Hanna", tone: "Friendly" },
];

const TRIGGER_OPTIONS: { value: VoiceReplyTrigger; label: string }[] = [
  { value: "voice_only", label: "When customer sends voice" },
  { value: "always", label: "Always" },
  { value: "on_request", label: "Only when requested" },
];

const CATEGORIES = ["family", "friend", "business", "vip", "other"] as const;

function navigateBack() {
  window.location.hash = "/settings";
}

export function VoiceSettings() {
  const { settings, fetchSettings, updateSettings, toggleVoiceReplies } =
    useVoiceStore();

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const isEnabled = settings.voiceRepliesEnabled;

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Voice Settings" onBack={navigateBack} />

      <motion.main
        variants={PAGE_VARIANTS}
        initial="hidden"
        animate="visible"
        className="flex-1 px-4 pb-20 pt-2"
      >
        <FeatureStatusBanner
          status="limited"
          title="Core voice settings synced / ዋና የድምጽ ቅንብሮች ተመሳስለዋል"
          description="Voice enablement and default voice sync with the API. Trigger and category preferences remain session-only. / ድምጽ ማንቃትና ነባሪ ድምጽ ከAPI ጋር ይመሳሰላሉ፤ ሌሎች ምርጫዎች ለክፍለ ጊዜው ብቻ ናቸው።"
        />

        <VoiceRepliesToggle enabled={isEnabled} onToggle={toggleVoiceReplies} />

        <AnimatePresence>
          {isEnabled && (
            <motion.div
              key="voice-options"
              variants={COLLAPSE_VARIANTS}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <DefaultVoiceSection
                selectedGender={settings.defaultVoiceGender}
                onSelect={(gender) => updateSettings({ defaultVoiceGender: gender })}
              />

              <ReplyTriggerSection
                selectedTrigger={settings.voiceReplyTrigger}
                onSelect={(trigger) =>
                  updateSettings({ voiceReplyTrigger: trigger })
                }
              />

              <LanguageAutoSwitchSection
                enabled={settings.languageAutoSwitch}
                onToggle={() =>
                  updateSettings({
                    languageAutoSwitch: !settings.languageAutoSwitch,
                  })
                }
              />

              <CategoryVoicesSection
                categoryVoices={settings.categoryVoices}
                onUpdateCategory={(category, gender) => {
                  const current = settings.categoryVoices[category] ?? {
                    gender: "female" as const,
                    tone: "neutral",
                  };
                  updateSettings({
                    categoryVoices: {
                      ...settings.categoryVoices,
                      [category]: { gender, tone: current.tone },
                    },
                  });
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.main>
    </div>
  );
}

function VoiceRepliesToggle({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-4">
      <div
        className={cn(
          "flex items-center justify-between rounded-[var(--radius-lg)] shadow-sm p-4",
          "bg-[var(--color-surface)]",
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)]",
              enabled
                ? "bg-blue-50 text-blue-600"
                : "bg-slate-50 text-slate-500",
            )}
          >
            {enabled ? (
              <Mic className="h-5 w-5" />
            ) : (
              <MicOff className="h-5 w-5" />
            )}
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-text)]">
              Voice Replies
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              {enabled ? "AI responds with voice messages" : "Voice replies are off"}
            </p>
          </div>
        </div>

        <ToggleSwitch
          enabled={enabled}
          onToggle={onToggle}
          ariaLabel="Toggle voice replies"
        />
      </div>
    </motion.section>
  );
}

function DefaultVoiceSection({
  selectedGender,
  onSelect,
}: {
  selectedGender: VoiceGender;
  onSelect: (gender: VoiceGender) => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Default Voice
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {VOICE_OPTIONS.map((voice) => {
          const isSelected = selectedGender === voice.gender;
          return (
            <button
              key={voice.gender}
              type="button"
              onClick={() => onSelect(voice.gender)}
              aria-label={`Select ${voice.name} voice`}
              aria-pressed={isSelected}
              className={cn(
                "flex flex-col items-center gap-2 rounded-[var(--radius-lg)] p-4",
                "transition-all",
                isSelected
                  ? "border-2 border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                  : "border-2 border-[var(--color-border)] bg-[var(--color-surface)]",
              )}
            >
              <div
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-full font-headline text-lg font-bold",
                  isSelected
                    ? "bg-blue-50 text-blue-600"
                    : "bg-slate-50 text-[var(--color-text-secondary)]",
                )}
              >
                {voice.gender === "male" ? "M" : "F"}
              </div>
              <span className="text-sm font-medium text-[var(--color-text)]">
                {voice.name}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {voice.tone}
              </span>
            </button>
          );
        })}
      </div>
    </motion.section>
  );
}

function ReplyTriggerSection({
  selectedTrigger,
  onSelect,
}: {
  selectedTrigger: VoiceReplyTrigger;
  onSelect: (trigger: VoiceReplyTrigger) => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        When to Reply with Voice
      </h2>
      <div className="flex flex-col gap-1.5">
        {TRIGGER_OPTIONS.map((option) => {
          const isSelected = selectedTrigger === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              aria-label={option.label}
              aria-pressed={isSelected}
              className={cn(
                "flex items-center gap-3 rounded-[var(--radius-md)] px-4 py-3",
                "transition-colors",
                isSelected
                  ? "bg-[var(--color-primary)]/5"
                  : "bg-[var(--color-surface)]",
              )}
            >
              <RadioDot selected={isSelected} />
              <span
                className={cn(
                  "text-sm",
                  isSelected
                    ? "font-medium text-[var(--color-primary)]"
                    : "text-[var(--color-text)]",
                )}
              >
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </motion.section>
  );
}

function LanguageAutoSwitchSection({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-4">
      <div
        className={cn(
          "flex items-center justify-between rounded-[var(--radius-lg)] shadow-sm p-4",
          "bg-[var(--color-surface)]",
        )}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-purple-50 text-purple-600">
            <Languages className="h-5 w-5" />
          </div>
          <div className="flex-1 pr-3">
            <h2 className="text-sm font-semibold text-[var(--color-text)]">
              Language Auto-Switch
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              Automatically switch voice language to match the customer's language
            </p>
          </div>
        </div>

        <ToggleSwitch
          enabled={enabled}
          onToggle={onToggle}
          ariaLabel="Toggle language auto-switch"
        />
      </div>
    </motion.section>
  );
}

function CategoryVoicesSection({
  categoryVoices,
  onUpdateCategory,
}: {
  categoryVoices: Record<string, { gender: VoiceGender; tone: string }>;
  onUpdateCategory: (category: string, gender: VoiceGender) => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Category Voice Preferences
      </h2>
      <div className="flex flex-col gap-2">
        {CATEGORIES.map((category) => {
          const voice = categoryVoices[category];
          if (!voice) return null;

          return (
            <div
              key={category}
              className={cn(
                "flex items-center justify-between rounded-[var(--radius-md)] px-4 py-3",
                "bg-[var(--color-surface)]",
              )}
            >
              <span className="text-sm font-medium capitalize text-[var(--color-text)]">
                {category}
              </span>
              <GenderPicker
                selected={voice.gender}
                onSelect={(gender) => onUpdateCategory(category, gender)}
                categoryName={category}
              />
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}

function ToggleSwitch({
  enabled,
  onToggle,
  ariaLabel,
}: {
  enabled: boolean;
  onToggle: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={ariaLabel}
      onClick={onToggle}
      className={cn(
        "relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full",
        "transition-colors duration-200",
        enabled ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]",
      )}
    >
      <motion.span
        layout
        transition={SPRING}
        className={cn(
          "block h-5 w-5 rounded-full bg-white shadow-sm",
          enabled ? "ml-6" : "ml-1",
        )}
      />
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <div
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
        selected
          ? "border-[var(--color-primary)]"
          : "border-[var(--color-text-muted)]",
      )}
    >
      {selected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={SPRING}
          className="h-2.5 w-2.5 rounded-full bg-[var(--color-primary)]"
        />
      )}
    </div>
  );
}

function GenderPicker({
  selected,
  onSelect,
  categoryName,
}: {
  selected: VoiceGender;
  onSelect: (gender: VoiceGender) => void;
  categoryName: string;
}) {
  return (
    <div className="flex gap-1" role="radiogroup" aria-label={`Voice gender for ${categoryName}`}>
      {(["male", "female"] as const).map((gender) => {
        const isSelected = selected === gender;
        return (
          <button
            key={gender}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={`${gender} voice for ${categoryName}`}
            onClick={() => onSelect(gender)}
            className={cn(
              "rounded-[var(--radius-sm)] px-3 py-1.5 text-xs font-medium",
              "transition-colors",
              isSelected
                ? "bg-[var(--color-primary)] text-white"
                : "bg-slate-50 text-[var(--color-text-secondary)]",
            )}
          >
            {gender === "male" ? "M" : "F"}
          </button>
        );
      })}
    </div>
  );
}
